import React, { useEffect, useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Gift, Download, Loader2 } from "lucide-react";
import logoFullLight from "../../assets/logo-light.png";
import logoFullDark from "../../assets/logo-dark.png";
import { toPng } from "html-to-image";
import { supabase } from "../lib/woocommerce";

export function WebPass({ userId }: { userId: string }) {
  const [isDarkMode, setIsDarkMode] = useState(true);

  const [isDownloading, setIsDownloading] = useState(false);
  const [dynamicConfig, setDynamicConfig] = useState<any>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const urlStore = searchParams.get('s');
        if (urlStore) {
          const { data, error } = await supabase.from('white_labels').select('*').eq('slug', urlStore.toLowerCase()).single();
          if (data && !error) {
            setDynamicConfig(data);
            // Also override the --brand color for the card
            if (data.brand_color) {
              document.documentElement.style.setProperty('--brand', data.brand_color);
              const hex = data.brand_color.replace('#', '');
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              document.documentElement.style.setProperty('--brand-color-rgb', `${r}, ${g}, ${b}`);
            }
          }
        }
      } catch (e) {
        console.error("Error fetching white label for wallet", e);
      } finally {
        setIsLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  const brandConfig = dynamicConfig || (window as any).__brandConfig;
  const storeLogo = brandConfig?.logo_url || (isDarkMode ? logoFullDark : logoFullLight);
  const storeName = brandConfig?.slug ? brandConfig.slug.charAt(0).toUpperCase() + brandConfig.slug.slice(1) : "LojaPilot";

  useEffect(() => {
    // Basic dark mode detection based on class or media query
    const checkDark = () => {
      const isDark = document.documentElement.classList.contains("dark") ||
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };
    checkDark();
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', checkDark);
    return () => media.removeEventListener('change', checkDark);
  }, []);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "transparent",
      });
      const link = document.createElement("a");
      link.download = `cartao-${storeName.toLowerCase()}-${userId}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Failed to generate image", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden font-inter">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#10B981]/20 rounded-full blur-[100px] -mr-20 -mt-20 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px] -ml-20 -mb-20 pointer-events-none" />

      {/* Logo Header */}
      <div className="mb-12 relative z-10">
        <img
          src={storeLogo}
          alt={storeName}
          className="h-10 object-contain drop-shadow-lg"
        />
      </div>

      {/* Card */}
      <div ref={cardRef} style={{ background: 'linear-gradient(to bottom, #111827, #000000)', borderColor: 'rgba(255,255,255,0.1)', boxShadow: '0 20px 60px -15px rgba(var(--brand-color-rgb),0.3)' }} className="relative w-full max-w-[340px] aspect-[1/1.5] rounded-3xl border flex flex-col p-8 z-10 overflow-hidden">
        {/* Subtle inner glows */}
        <div style={{ backgroundColor: 'var(--brand)', opacity: 0.3 }} className="absolute top-[-50px] right-[-50px] w-32 h-32 rounded-full blur-2xl" />
        <div style={{ backgroundColor: 'var(--brand)', opacity: 0.2 }} className="absolute bottom-[-50px] left-[-50px] w-32 h-32 rounded-full blur-2xl" />

        <div className="flex justify-between items-start mb-10 relative z-20">
          <div>
            <h4 className="font-['Poppins'] font-semibold text-2xl text-white tracking-wide">{storeName}</h4>
            <p className="text-[10px] uppercase font-['Inter'] text-brand tracking-widest mt-1">Premium Card</p>
          </div>
          <Gift className="w-8 h-8 text-brand opacity-90" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center relative z-20">
          <div className="p-4 bg-white rounded-2xl shadow-xl flex items-center justify-center transform transition-transform duration-300 hover:scale-105">
            <QRCodeSVG
              value={JSON.stringify({ customerId: userId })}
              size={200}
              level="H"
              includeMargin={false}
              fgColor="#000000"
              bgColor="#ffffff"
            />
          </div>
        </div>

        <div className="mt-auto pt-6 border-t border-white/10 relative z-20 text-center">
          <p className="text-sm text-white/60 font-inter">
            Apresente este código na caixa para acumular ou resgatar pontos.
          </p>
        </div>
      </div>

      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="mt-8 relative z-10 flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-medium transition-colors shadow-lg shadow-brand/20 disabled:opacity-50"
      >
        <Download className="w-4 h-4" />
        {isDownloading ? "A processar..." : "Guardar como Imagem"}
      </button>
    </div>
  );
}
